import { useCallback, useEffect, useRef, useState } from "react";
import apiClient from "@shared/services/apiClient";
import { scheduleStudyVideoPlaybackWarmup } from "../services/studyVideoPlaybackWarmupClient";
import { VideoRecording } from "../types/video.types";
import {
  filterPlayableStudyVideos,
  normalizeStudyVideoPayload,
  summarizeStudyVideoPlaybackReadiness,
} from "../utils/videoSyncUtils";

interface UseStudyVideoPlaybackWarmupOptions {
  enabled?: boolean;
}

export interface StudyVideoPlaybackWarmupState {
  videos: VideoRecording[];
  isReady: boolean;
  isWarming: boolean;
  readyCount: number;
  totalCount: number;
  refresh: () => Promise<void>;
}

const WARM_POLL_INTERVAL_MS = 2500;

export function useStudyVideoPlaybackWarmup(
  studyId: string | undefined,
  options: UseStudyVideoPlaybackWarmupOptions = {},
): StudyVideoPlaybackWarmupState {
  const enabled = Boolean(studyId) && (options.enabled ?? true);
  const [videos, setVideos] = useState<VideoRecording[]>([]);
  const [readiness, setReadiness] = useState({
    isReady: false,
    isWarming: false,
    readyCount: 0,
    totalCount: 0,
  });

  const readinessRef = useRef(readiness);
  readinessRef.current = readiness;

  const refresh = useCallback(async () => {
    if (!studyId || !enabled) {
      return;
    }

    const response = await apiClient.get(`/video-eeg/study/${studyId}`);
    const normalized = normalizeStudyVideoPayload(response.data);
    const playable = filterPlayableStudyVideos(normalized);
    const summary = summarizeStudyVideoPlaybackReadiness(playable);

    setVideos(playable);
    setReadiness({
      isReady: summary.isReady,
      isWarming: summary.isWarming,
      readyCount: summary.readyCount,
      totalCount: summary.total,
    });
  }, [enabled, studyId]);

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!enabled || !studyId) {
      setVideos([]);
      setReadiness({
        isReady: false,
        isWarming: false,
        readyCount: 0,
        totalCount: 0,
      });
      return;
    }

    let cancelled = false;
    let timer: number | null = null;

    const stopPolling = () => {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    const poll = async () => {
      if (cancelled) return;
      await refreshRef.current();
      if (!cancelled && readinessRef.current.isReady) {
        stopPolling();
      }
    };

    const start = async () => {
      scheduleStudyVideoPlaybackWarmup([studyId]);

      await poll();

      if (!cancelled && !readinessRef.current.isReady) {
        timer = window.setInterval(() => {
          void poll();
        }, WARM_POLL_INTERVAL_MS);
      }
    };

    void start();

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [enabled, studyId]);

  return {
    videos,
    isReady: readiness.isReady,
    isWarming: readiness.isWarming,
    readyCount: readiness.readyCount,
    totalCount: readiness.totalCount,
    refresh,
  };
}
